import React, { useEffect, useState } from 'react';
import { clampSeats, seatLimits } from './tableGeometry';
import type { TableShape } from './tableGeometry';

interface SeatCountInputProps {
    shape: TableShape | undefined;
    value: number | undefined;
    disabled?: boolean;
    onCommit: (seats: number) => void;
}

/**
 * Seat count for the selected table. Typing is free; the value is clamped to the
 * shape's range (crescent 4 to 12, rectangular 8 to 16, ...) when the field is
 * left or Enter is pressed, so typing "12" is not cut short at the first digit.
 */
export const SeatCountInput: React.FC<SeatCountInputProps> = ({ shape, value, disabled, onCommit }) => {
    const { min, max } = seatLimits(shape);
    const [draft, setDraft] = useState(value == null ? '' : String(value));
    useEffect(() => { setDraft(value == null ? '' : String(value)); }, [value]);

    const commit = () => {
        if (draft.trim() === '') { setDraft(value == null ? '' : String(value)); return; }
        const seats = clampSeats(shape, Number(draft));
        setDraft(String(seats));
        if (seats !== value) onCommit(seats);
    };

    return (
        <input
            type="number"
            min={min}
            max={max}
            className="fp-zoom-seats-input"
            title={`Seats (${min} to ${max})`}
            aria-label="Seats"
            disabled={disabled}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={e => {
                e.stopPropagation();
                if (e.key === 'Enter') { e.preventDefault(); commit(); }
            }}
        />
    );
};

export default SeatCountInput;
